import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import { getHeatmap } from "@/lib/analyticsApi";
import { formatError } from "@/lib/api";
import { NEON, Card, Label, Empty, fmtNum } from "./shared";

/* Logical render width for the embedded page; the iframe is rendered at this
   width and scaled down to fit its container so points map consistently. */
const LOGICAL_W = 1280;
const KINDS = [
  { id: "all", label: "All" },
  { id: "click", label: "Clicks" },
  { id: "move", label: "Moves" },
];

/* Paint soft radial gradients onto the canvas. Clicks weigh heavier so they
   read hotter than passive mouse moves. */
function paintHeatmap(canvas, points, w, h) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.clearRect(0, 0, w, h);
  if (!points || points.length === 0) return;

  const clickR = Math.max(34, w * 0.03);
  const moveR = Math.max(22, w * 0.018);

  ctx.globalCompositeOperation = "lighter";
  for (const p of points) {
    const x = Math.min(w, Math.max(0, p.xpct * w));
    const y = Math.min(h, Math.max(0, p.ypct * h));
    const isClick = p.kind === "click";
    const r = isClick ? clickR : moveR;
    const alpha = isClick ? 0.5 : 0.18;
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, `rgba(255,255,255,${alpha})`);
    g.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalCompositeOperation = "source-over";

  // Colorize the accumulated intensity into a warm gradient.
  const imageData = ctx.getImageData(0, 0, w, h);
  const d = imageData.data;
  for (let i = 0; i < d.length; i += 4) {
    const a = d[i + 3];
    if (a === 0) continue;
    const t = Math.min(1, a / 255);
    // cool (blue) -> green -> yellow -> red as t increases
    let r, g, b;
    if (t < 0.33) { const k = t / 0.33; r = 0; g = Math.round(120 + 135 * k); b = Math.round(255 - 100 * k); }
    else if (t < 0.66) { const k = (t - 0.33) / 0.33; r = Math.round(200 * k); g = 255; b = Math.round(155 - 155 * k); }
    else { const k = (t - 0.66) / 0.34; r = Math.round(200 + 55 * k); g = Math.round(255 - 200 * k); b = 0; }
    d[i] = r; d[i + 1] = g; d[i + 2] = b;
    d[i + 3] = Math.min(220, Math.round(60 + t * 200));
  }
  ctx.putImageData(imageData, 0, 0);
}

const HeatmapView = ({ days }) => {
  const [path, setPath] = useState("/");
  const [kind, setKind] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [docHeight, setDocHeight] = useState(900);
  const [scale, setScale] = useState(1);

  const wrapRef = useRef(null);
  const iframeRef = useRef(null);
  const canvasRef = useRef(null);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    getHeatmap(path, days, kind)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => toast.error(formatError(e?.response?.data?.detail, "Failed to load heatmap.")))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [path, days, kind]);

  useEffect(() => load(), [load]);

  // Fit the 1280px-logical iframe into the available container width.
  useEffect(() => {
    const measure = () => {
      if (!wrapRef.current) return;
      const avail = wrapRef.current.clientWidth;
      setScale(Math.min(1, avail / LOGICAL_W));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Read the embedded document height (same-origin) to size the canvas.
  // Keep the tallest reading so async SPA rendering can only grow the stage.
  const measureDocHeight = useCallback(() => {
    try {
      const doc = iframeRef.current?.contentWindow?.document;
      if (doc) {
        const h = Math.max(
          doc.documentElement?.scrollHeight || 0,
          doc.body?.scrollHeight || 0,
          600
        );
        setDocHeight((prev) => (h > prev ? h : prev));
      }
    } catch {
      // cross-origin (shouldn't happen, same origin) — keep default height
    }
  }, []);

  // The embedded site is a React SPA that keeps rendering after the iframe's
  // load event, so re-measure a few times and keep the tallest height.
  const onIframeLoad = useCallback(() => {
    measureDocHeight();
    [400, 1000, 2000].forEach((t) => window.setTimeout(measureDocHeight, t));
  }, [measureDocHeight]);

  // Reset the height when switching pages so a shorter page isn't left tall.
  useEffect(() => {
    setDocHeight(600);
  }, [path]);

  // Paint whenever data or sizing changes.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const w = LOGICAL_W;
    const h = docHeight;
    canvas.width = w;
    canvas.height = h;
    paintHeatmap(canvas, data?.points || [], w, h);
  }, [data, docHeight]);

  const paths = data?.paths_available || [];
  const src = window.location.origin + path;

  return (
    <div className="space-y-6">
      <Card
        title="Click & Move Heatmap"
        action={
          <button
            onClick={load}
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase font-bold text-white/70 hover:text-[#c8ff2a] transition-colors"
          >
            <RefreshCw size={13} /> Refresh
          </button>
        }
      >
        <div className="flex flex-wrap items-end gap-5">
          <div>
            <Label className="mb-2">Page</Label>
            <select
              value={path}
              onChange={(e) => setPath(e.target.value)}
              className="px-3 py-2 rounded-lg bg-[#0d0d0d] border border-white/15 text-white text-sm focus:border-[#c8ff2a] focus:outline-none min-w-[220px]"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              {paths.length === 0 && <option value="/">/</option>}
              {(paths.includes(path) ? paths : [path, ...paths]).map((p) => (
                <option key={p} value={p} style={{ background: "#0d0d0d" }}>{p}</option>
              ))}
            </select>
          </div>

          <div>
            <Label className="mb-2">Type</Label>
            <div className="inline-flex rounded-full overflow-hidden border border-white/12">
              {KINDS.map((k) => (
                <button
                  key={k.id}
                  onClick={() => setKind(k.id)}
                  className="px-3 py-2 text-[11px] tracking-[0.18em] uppercase font-bold transition-colors"
                  style={{
                    background: kind === k.id ? NEON : "rgba(255,255,255,0.05)",
                    color: kind === k.id ? "#060606" : "rgba(255,255,255,0.7)",
                  }}
                >
                  {k.label}
                </button>
              ))}
            </div>
          </div>

          <div className="ml-auto text-right">
            <Label className="mb-1">Points</Label>
            <div
              className="font-black tabular-nums"
              style={{ fontFamily: "'Azeret Mono', monospace", fontSize: "1.4rem", color: NEON }}
            >
              {loading ? <Loader2 size={18} className="animate-spin inline" /> : fmtNum(data?.count || 0)}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 text-[10px] tracking-[0.18em] uppercase text-white/45 font-bold">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm" style={{ background: "linear-gradient(90deg,#0078ff,#ff2a00)" }} /> cool → hot
          </span>
          <span>clicks weighted heavier than moves</span>
        </div>
      </Card>

      <Card title={null}>
        {paths.length === 0 && (data?.count || 0) === 0 ? (
          <Empty label="No heatmap points captured yet. Interact with the public site to gather data." />
        ) : (
          <div ref={wrapRef} className="w-full overflow-hidden rounded-xl border border-white/10">
            {/* Scaled stage: iframe + canvas overlay share the same logical box. */}
            <div
              style={{
                width: LOGICAL_W * scale,
                height: docHeight * scale,
                position: "relative",
                margin: "0 auto",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: LOGICAL_W,
                  height: docHeight,
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                }}
              >
                <iframe
                  ref={iframeRef}
                  title="heatmap-page"
                  src={src}
                  onLoad={onIframeLoad}
                  style={{
                    width: LOGICAL_W,
                    height: docHeight,
                    border: "0",
                    display: "block",
                    background: "#060606",
                  }}
                />
                <canvas
                  ref={canvasRef}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: LOGICAL_W,
                    height: docHeight,
                    pointerEvents: "none",
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default HeatmapView;
